import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import LazyLoadingSpinner from "components/LazyLoadingSpinner";
import { useAuth } from "components/AuthProvider";
import { goToSelectBranch } from "services/session";
import { fetchLocation, fetchLocations } from "store/actions";
import { _updateTheme } from "utils";
import { TAB_SYNC_KEY } from "utils/constants";
import { socketIo } from "socket";

const SELECTED_BRANCH_KEY = "hospital-selected-branch";
const PENDING_LOCATION_KEY = "hospital-pending-location-switch";
const LOCAL_SWITCH_TTL = 5000;
const LocationSessionContext = createContext(null);

const resolveLocationPath = (entity) => {
  if (entity?.nextSection && typeof entity.nextSection === "string") return "/onboarding";
  return "/modules";
};

/**
 * @param {string} branchId
 */
const broadcastBranch = (branchId) => {
  localStorage.setItem(TAB_SYNC_KEY, JSON.stringify({ branchId, timestamp: Date.now() }));
};

/**
 * @param {string} branchId
 */
const persistSelectedBranch = (branchId) => {
  localStorage.setItem(SELECTED_BRANCH_KEY, branchId);
};

/**
 * @param {LocationSessionProviderProps} props
 */
export const LocationSessionProvider = (props) => {
  const { children } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const company = useSelector((state) => state.company);
  const locations = useSelector((state) => state.company?.locations) ?? [];
  const orgBranches = useSelector((state) => state.user?.orgBranches) ?? [];
  const current = useSelector((state) => state.user?.current);
  const { user } = useAuth();
  // `/me` calls the email `username`; the bootstrap mirrors it into localStorage
  // for the reducers and older call sites that still read the key directly.
  const email = user?.username ?? localStorage.getItem("email");
  const currentBranchId = current?.value ?? null;
  const currentNextSection = current?.nextSection;
  const profileId = current?.profile?._id ?? current?.details?.userId;
  const isSelectBranchPage = location.pathname === "/select-branch";
  // Used to be `Boolean(token && email)` — both artifacts of a login this app
  // no longer performs. A known identity is the gate now; the bootstrap below
  // proves the session for real, and a 401 there signs the user out.
  const isSessionReady = Boolean(email);
  const [isBootstrapping, setIsBootstrapping] = useState(isSessionReady);
  const [isSwitching, setIsSwitching] = useState(false);
  const [syncLocation, setSyncLocation] = useState(null);
  const hasResolvedInitialLocationRef = useRef(false);
  const currentBranchIdRef = useRef(currentBranchId);
  const currentNextSectionRef = useRef(currentNextSection);
  const isSwitchingRef = useRef(isSwitching);
  const lastLocalSwitchRef = useRef(/** @type {LocationSwitchSnapshot | null} */ (null));

  currentBranchIdRef.current = currentBranchId;
  currentNextSectionRef.current = currentNextSection;
  isSwitchingRef.current = isSwitching;

  const switchLocation = useCallback(
    async (locationId, options = {}) => {
      if (!locationId) return null;

      const shouldBroadcast = options.broadcast !== false;
      const nextPath =
        options.path ??
        (options.navigate === false
          ? null
          : resolveLocationPath(options.entity ?? { nextSection: currentNextSection }));

      if (locationId === currentBranchId) {
        persistSelectedBranch(locationId);
        sessionStorage.removeItem(PENDING_LOCATION_KEY);
        if (nextPath && nextPath !== history.location.pathname) {
          history.push(nextPath);
        }
        if (shouldBroadcast) {
          broadcastBranch(locationId);
        }
        return { _id: locationId, nextSection: currentNextSection };
      }

      lastLocalSwitchRef.current = {
        at: Date.now(),
        fromId: currentBranchId,
        toId: locationId,
      };
      setIsSwitching(true);
      sessionStorage.setItem(PENDING_LOCATION_KEY, locationId);

      try {
        const locationData = await dispatch(fetchLocation(locationId));
        const nextLocation = locationData?.currentLocation;

        if (!nextLocation?._id) {
          sessionStorage.removeItem(PENDING_LOCATION_KEY);
          return null;
        }

        persistSelectedBranch(nextLocation._id);
        sessionStorage.removeItem(PENDING_LOCATION_KEY);
        lastLocalSwitchRef.current = {
          at: Date.now(),
          fromId: currentBranchId,
          toId: nextLocation._id,
        };

        const redirectPath = options.path ?? (options.navigate === false ? null : resolveLocationPath(nextLocation));

        if (shouldBroadcast) broadcastBranch(nextLocation._id);
        if (redirectPath && redirectPath !== history.location.pathname) history.push(redirectPath);

        return nextLocation;
      } finally {
        setIsSwitching(false);
      }
    },
    [broadcastBranch, currentBranchId, currentNextSection, dispatch, history, persistSelectedBranch],
  );

  const acceptSyncLocation = useCallback(async () => {
    if (!syncLocation?._id) return;

    await switchLocation(syncLocation._id, { broadcast: false, path: "/modules" });
    setSyncLocation(null);
  }, [switchLocation, syncLocation]);

  useEffect(() => {
    if (company?.error === null && company?.loading === false) {
      _updateTheme(company?.primaryColour ?? "#6a69e4", company?.secondaryColour ?? "#fa4659");
    }
  }, [company?.error, company?.loading, company?.primaryColour, company?.secondaryColour]);

  useEffect(() => {
    let isActive = true;

    const bootstrapLocationSession = async () => {
      hasResolvedInitialLocationRef.current = true;
      setIsBootstrapping(true);
      try {
        const nextLocations = await dispatch(fetchLocations(email));
        if (!isActive) return;

        const storedBranchId = localStorage.getItem(SELECTED_BRANCH_KEY);
        const nextBranchId =
          nextLocations.find((branch) => branch._id === storedBranchId)?._id ??
          (nextLocations.length === 1 ? nextLocations[0]?._id : null);
        const isSingleLocation = nextLocations.length === 1;

        if (!nextBranchId) {
          localStorage.removeItem(SELECTED_BRANCH_KEY);
          if (!isSelectBranchPage) {
            // The SPA owns the branch picker, so this has to leave the app.
            goToSelectBranch();
          }
          return;
        }

        if (nextBranchId === currentBranchIdRef.current) {
          if (isSelectBranchPage && isSingleLocation) {
            history.replace(resolveLocationPath({ nextSection: currentNextSectionRef.current }));
          }
          return;
        }

        await switchLocation(nextBranchId, {
          broadcast: false,
          navigate: isSelectBranchPage && isSingleLocation,
        });
      } finally {
        setIsBootstrapping(false);
      }
    };

    if (!isSessionReady) {
      hasResolvedInitialLocationRef.current = false;
      setIsBootstrapping(false);
      return () => (isActive = false);
    }

    if (!hasResolvedInitialLocationRef.current) {
      void bootstrapLocationSession();
    }

    return () => {
      isActive = false;
    };
  }, [dispatch, email, history, isSelectBranchPage, isSessionReady, switchLocation]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TAB_SYNC_KEY || !event.newValue) return;
      let nextBranchId = null;
      try {
        nextBranchId = JSON.parse(event.newValue)?.branchId ?? null;
      } catch (error) {
        nextBranchId = null;
      }

      if (nextBranchId && nextBranchId !== currentBranchId) {
        void switchLocation(nextBranchId, { broadcast: false, path: "/modules" });
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentBranchId, switchLocation]);

  useEffect(() => {
    if (!current?.parentOrganizationId || !profileId) return;

    const socket = socketIo(`?channelName=${profileId}-${current.parentOrganizationId}`);

    socket.on("location-sync-required", (data) => {
      const nextLocation = data?.nextLocation ?? null;
      const pendingLocationId = sessionStorage.getItem(PENDING_LOCATION_KEY);
      const activeBranchId = currentBranchIdRef.current;
      const lastLocalSwitch = lastLocalSwitchRef.current;
      const isRecentLocalSwitch = lastLocalSwitch && Date.now() - lastLocalSwitch.at < LOCAL_SWITCH_TTL;
      const matchesLastLocalSwitch =
        nextLocation?._id === lastLocalSwitch?.toId || nextLocation?._id === lastLocalSwitch?.fromId;

      if (!nextLocation?._id || nextLocation._id === activeBranchId) return;

      if (pendingLocationId === nextLocation._id) {
        sessionStorage.removeItem(PENDING_LOCATION_KEY);
        return;
      }

      if (matchesLastLocalSwitch && (isRecentLocalSwitch || isSwitchingRef.current)) {
        return;
      }

      setSyncLocation(nextLocation);
    });

    return () => {
      socket.disconnect();
    };
  }, [current?.parentOrganizationId, profileId]);

  const value = {
    acceptSyncLocation,
    currentBranchId,
    isBootstrapping,
    isSwitching,
    locations,
    orgBranches,
    setSyncLocation,
    switchLocation,
    syncLocation,
  };

  if (isBootstrapping) return <LazyLoadingSpinner text="Bootstraping..." />;

  return <LocationSessionContext.Provider value={value}>{children}</LocationSessionContext.Provider>;
};

LocationSessionProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export const useLocationSession = () => {
  const context = useContext(LocationSessionContext);

  if (!context) {
    throw new Error("useLocationSession must be used within LocationSessionProvider");
  }

  return context;
};

/**
 * @typedef {Object} LocationSessionProviderProps
 * @property {React.ReactNode} children
 */

/**
 * @typedef LocationSwitchSnapshot
 * @property {string | null} fromId
 * @property {string} toId
 * @property {number} at
 */
